import {
  FetchGuideParams,
  Guide,
  guidesQueryOptions,
  processWelcomeDiscountQueryOptions,
} from '@blesk-frontend/api';
import {
  Docs,
  Favorite,
  Loader,
  Page,
  PageEmptyState,
  PageTitle,
  Pagination,
  Search,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Trash,
  useModal,
} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from '@tanstack/react-router';
import {useEffect, useMemo} from 'react';
import {AppPageWrapper} from '@/components';
import {BillingDiscountModal} from '@/features/billing';
import {GuideCard, GuideListTopbar, guidesListViewStore} from '@/features/guides';
import {PlanPaywallModal} from '@/features/plans';
import {ChangedTeamHint} from '@/features/team';
import {useAppPlatform} from '@/hooks';
import {Route} from '@/routes/_app/index';
import {GuidesZeroState} from '../components';
import {GuidesZeroInstalledState} from '../components/GuidesZeroInstalledState';

const getTitleByFilter = (params?: FetchGuideParams) => {
  if (params?.filter?.search) {
    return {label: 'Поиск'};
  }

  if (params?.filter?.favorites) {
    return {label: 'Избранное'};
  }

  if (params?.filter?.trash) {
    return {label: 'Корзина'};
  }

  return {label: 'Инструкции'};
};

const getEmptyStateByFilter = (params?: FetchGuideParams) => {
  if (params?.filter?.search) {
    return {
      icon: Search,
      title: 'Ничего не найдено',
      text: 'Попробуйте изменить поисковый запрос',
    };
  }

  if (params?.filter?.favorites) {
    return {
      icon: Favorite,
      title: 'Здесь пока ничего нет',
      text: 'Инструкции отмеченные звёздочкой появятся здесь',
    };
  }

  if (params?.filter?.trash) {
    return {
      icon: Trash,
      title: 'Здесь пока ничего нет',
      text: 'Удалённые инструкции появятся здесь',
    };
  }

  return {
    icon: Docs,
    title: 'Здесь пока ничего нет',
    text: 'Начните работу с создания вашей первой пошаговой инструкции',
  };
};

export const RenderGrid = ({guides, isLoading}: {guides: Guide[]; isLoading: boolean}) => {
  return (
    <div className="relative grid flex-1 auto-rows-max gap-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center backdrop-blur-xs">
          <Loader size={32} />
        </div>
      )}

      {guides.map((guide) => (
        <GuideCard key={`guide-card-${guide.id}`} guide={guide} />
      ))}
    </div>
  );
};

export const GuidesListPage = () => {
  const {view} = guidesListViewStore();
  const navigate = useNavigate({from: Route.fullPath});
  const {limit} = Route.useSearch() || {};
  const {params} = Route.useLoaderDeps();
  const {extensionStatus} = useAppPlatform();
  const isInstalledExtension = extensionStatus === 'installed';

  const {data: guides, isLoading} = useQuery(
    guidesQueryOptions({...params, take: view === 'grid' ? 20 : 24}),
  );

  const title = useMemo(() => getTitleByFilter(params), [params]);
  const emptyState = useMemo(() => getEmptyStateByFilter(params), [params]);

  const isMainPage = useMemo(
    () => !params?.filter?.search && !params?.filter?.favorites && !params?.filter?.trash,
    [params],
  );

  const isZeroState = !isLoading && isMainPage && guides?.data.length === 0;

  const onChangeSort = async (sort: string) => {
    localStorage.setItem('guides-list-sort', sort);
    await navigate({
      to: '.',
      search: (prev) => ({...prev, sort: sort as FetchGuideParams['sort']}),
    });
  };

  const openBillingDiscountModal = useModal<{discountId: string}>((props) => (
    <BillingDiscountModal {...props} />
  ));

  const {data: welcomeDiscount} = useQuery(
    processWelcomeDiscountQueryOptions({
      type: 'discount',
      payload: 'welcome',
    }),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (welcomeDiscount?.id) {
      openBillingDiscountModal({discountId: welcomeDiscount.id});
    }
  }, [welcomeDiscount]);

  const openPaywallModal = useModal((props) => <PlanPaywallModal {...props} />);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (limit) {
      openPaywallModal();
    }
  }, [limit]);

  if (isZeroState) {
    return (
      <AppPageWrapper>
        <ChangedTeamHint />
        {isInstalledExtension ? <GuidesZeroInstalledState /> : <GuidesZeroState />}
      </AppPageWrapper>
    );
  }

  return (
    <Page className="flex h-full flex-col">
      <GuideListTopbar />

      <div className="container mx-auto flex h-full max-w-screen-xl flex-col gap-8">
        <PageTitle
          extra={
            !!guides?.data &&
            !!guides?.data.length && (
              <Select value={params.sort} onValueChange={onChangeSort}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="По дате изменения" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="updatedAt">По дате изменения</SelectItem>
                  <SelectItem value="createdAt">По дате создания</SelectItem>
                  <SelectItem value="name">По алфавиту</SelectItem>
                </SelectContent>
              </Select>
            )
          }
        >
          <span>{title.label}</span>
        </PageTitle>

        <ChangedTeamHint />

        {!isLoading && (!guides?.data || guides?.data.length === 0) ? (
          <PageEmptyState {...emptyState} />
        ) : (
          <RenderGrid
            key={`render-grid-${title.label}`}
            isLoading={isLoading}
            guides={guides?.data || []}
          />
        )}

        {guides?.pagination && <Pagination pagination={guides.pagination} />}
      </div>
    </Page>
  );
};
